import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';
export type ErrorType = 'frontend' | 'edge_function' | 'database';

interface LogErrorParams {
  type: ErrorType;
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  severity?: ErrorSeverity;
}

// Sanitize error context to remove sensitive data
const sanitizeContext = (context?: Record<string, unknown>): Record<string, unknown> => {
  if (!context) return {};
  
  const sanitized = { ...context };
  const sensitiveKeys = ['password', 'token', 'api_key', 'apiKey', 'secret', 'authorization', 'auth'];
  
  Object.keys(sanitized).forEach(key => {
    if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    }
  });
  
  return sanitized;
};

// Truncate stack trace in production
const sanitizeStack = (stack?: string): string | null => {
  if (!stack) return null;
  
  // In production, limit stack trace length
  if (import.meta.env.PROD) {
    return stack.substring(0, 500);
  }
  
  return stack;
};

export const logError = async (params: LogErrorParams) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase.from('error_logs').insert({
      user_id: user?.id || null,
      error_type: params.type,
      error_message: params.message,
      error_stack: sanitizeStack(params.stack),
      error_context: sanitizeContext(params.context),
      severity: params.severity || 'error',
      user_agent: navigator.userAgent,
      url: window.location.href,
    });

    if (error) {
      logger.error('Failed to log error', { error });
    }
  } catch (e) {
    logger.error('Error logging failed', { error: e });
  }
};

export const logFrontendError = (error: Error, context?: Record<string, unknown>) => {
  logError({
    type: 'frontend',
    message: error.message,
    stack: error.stack,
    context,
    severity: 'error',
  });
};

export const logCriticalError = (message: string, context?: Record<string, unknown>) => {
  logError({
    type: 'frontend',
    message,
    context,
    severity: 'critical',
  });
};
